'use client';

import { Button } from '@tranquility-ai/theme-components/button';
import {
  UfdrViewer,
  type UfdrArtifactView,
  type UfdrChatMessage,
  type UfdrChatSummary,
  type UfdrSectionStatus,
  type UfdrTableModel,
} from '@tranquility-ai/theme-components/ufdr-viewer';
import type {
  MobileForensicFamily,
  MobileForensicStatus,
  MobileForensicViewKind,
  MobileForensicViewModel,
} from '@tranquility-ai/theme-components';
import { SpinnerGapIcon, XIcon } from '@phosphor-icons/react';

import { CitationHydratedPane } from '@/components/mobile-viewer/components/CitationHydratedPane';
import { useMediaPlayerTransitionEnabled } from '@/hooks/useMediaPlayerTransition';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { artifactFamily, artifactViews } from '@/shared/mobile-forensic/familyRegistry';
import { processedViewModel } from '@/shared/mobile-forensic/toViewModel';
import { useMobileUiConfigArtifact } from '@/shared/mobile-service/hooks/useMobileUiConfig';
import type { MobileEvidence, Source } from '@/shared/types';
import { GENERIC_PLATFORM, toPlatformPost } from '@/shared/ufdr/toPlatformPost';

import {
  citationConclusion,
  formatEvidenceJson,
  isHydratableArtifactCitation,
} from './citation-evidence';
import { QuerySummaries } from './QuerySummaries';
import { useHydratedArtifactEvidence, HYDRATE_TABLE_PAGE_SIZE } from './useHydratedArtifactEvidence';
import { inferCitationMapView, mapRowsToPoints } from '@/shared/mobile-service/lib/map-points';
import { groupHydratedMessageRows } from '@/shared/mobile-service/lib/message-mapping';
import type {
  MobileFrontEndBubbleMessageView,
  MobileFrontEndMapView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { useMobileUiConfig } from '@/shared/mobile-service/hooks/useMobileUiConfig';

function messageViewOf(artifact: MobileUiConfigArtifact | undefined): MobileFrontEndBubbleMessageView | undefined {
  const view = artifact?.front_end?.views?.find((entry) => entry.name === 'messages');
  if (!view || !('body_field' in view)) return undefined;
  return view;
}

function mapViewOf(artifact: MobileUiConfigArtifact | undefined): MobileFrontEndMapView | undefined {
  const view = artifact?.front_end?.views?.find((entry) => entry.name === 'map');
  if (!view || !('latitude_field' in view)) return undefined;
  return view;
}

function citedFamily(
  artifact: MobileUiConfigArtifact | undefined,
  view: MobileForensicViewKind,
): MobileForensicFamily {
  if (artifact) return artifactFamily(artifact);
  if (view === 'messages') return 'message';
  if (view === 'map') return 'location';
  return 'other';
}

function citationStatus(
  status: ReturnType<typeof useHydratedArtifactEvidence>['status'],
  errorMessage: string | null,
  loading: string,
  error: string,
  empty: string,
): MobileForensicStatus | undefined {
  if (status === 'loading') return { kind: 'loading', message: loading };
  if (status === 'error') return { kind: 'error', message: errorMessage ?? error };
  if (status === 'empty') return { kind: 'empty', message: empty };
  return undefined;
}

function publishedView(view: string | undefined): UfdrArtifactView {
  if (view === 'messages' || view === 'map' || view === 'gallery' || view === 'timeline' || view === 'cards' || view === 'table') {
    return view;
  }
  return 'table';
}

function publishedStatus(status: { kind?: string; message?: string } | undefined): UfdrSectionStatus | undefined {
  if (status?.kind === 'loading' || status?.kind === 'error' || status?.kind === 'empty') {
    return { status: status.kind, message: status.message };
  }
  return undefined;
}

function publishedTable(table: MobileForensicViewModel['table']): UfdrTableModel | undefined {
  if (!table) return undefined;
  return {
    columns: table.columns.map((column) => ({ id: column.id, label: column.label })),
    rows: table.rows.map((row) => ({
      id: row.id,
      cells: Object.fromEntries(Object.entries(row.cells).map(([key, cell]) => {
        const value = cell && typeof cell === 'object' && 'value' in cell ? cell.value : '';
        return [key, value == null ? '' : String(value)];
      })),
    })),
    total: table.page.total,
    query: { page: table.page.page, pageSize: table.page.pageSize },
  };
}

function publishedMessage(message: {
  id?: string;
  direction?: string;
  text?: string;
  timestamp?: string;
  sender?: { name?: string };
}): UfdrChatMessage {
  return {
    id: message.id ?? '',
    direction: message.direction === 'outgoing' ? 'outgoing' : 'incoming',
    text: message.text,
    timestamp: message.timestamp,
    sender: message.sender?.name,
  };
}

function publishedChats(model: MobileForensicViewModel): {
  chats: UfdrChatSummary[];
  openChat?: UfdrChatSummary;
} {
  const active = model.activeThread;
  const activeMessages = active?.data?.messages?.map((message) => publishedMessage(message));
  const chats = (model.threads ?? []).map((thread) => ({
    id: thread.id,
    app: thread.label || thread.platform || thread.title,
    platform: thread.platform,
    peer: thread.title,
    lastMessage: thread.lastMessage,
    timestamp: thread.timestamp,
    messageCount: thread.count,
    messages: active?.id === thread.id ? activeMessages : undefined,
  }));
  const openChat = active
    ? chats.find((chat) => chat.id === active.id) ?? {
        id: active.id,
        app: active.label || active.platform || active.data?.author?.name || active.id,
        platform: active.platform,
        peer: active.data?.author?.name || active.id,
        messages: activeMessages,
      }
    : undefined;
  if (openChat && !chats.some((chat) => chat.id === openChat.id)) chats.push(openChat);
  return { chats, openChat };
}

function hydratePageSize(limit: number | undefined): number {
  return limit && limit > 0 ? limit : HYDRATE_TABLE_PAGE_SIZE;
}

function fallbackTable(
  hydrated: ReturnType<typeof useHydratedArtifactEvidence>,
): MobileForensicViewModel['table'] {
  const sample = hydrated.items[0];
  const pageSize = hydratePageSize(hydrated.limit);
  const page = Math.floor(hydrated.offset / pageSize) + 1;
  const keys = Object.keys(sample ?? {}).filter((key) => key !== 'mobileArtifactId').slice(0, 8);
  return {
    columns: keys.map((id) => ({ id, label: id, render: 'text' as const })),
    rows: hydrated.items.map((row, index) => ({
      id: String(row.mobileArtifactId ?? row.id ?? index),
      cells: Object.fromEntries(keys.map((key) => {
        const value = row[key];
        const text = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value);
        return [key, { kind: 'text' as const, value: text }];
      })),
    })),
    page: { page, pageSize, total: hydrated.total },
  };
}

function citedMessages(
  artifact: MobileUiConfigArtifact | undefined,
  hydrated: ReturnType<typeof useHydratedArtifactEvidence>,
  threadId: string | undefined,
): Pick<MobileForensicViewModel, 'threads' | 'activeThread'> {
  const messageView = messageViewOf(artifact);
  if (!messageView) return {};
  const groups = groupHydratedMessageRows(hydrated.items, messageView, 'mobileArtifactId');
  const threads = groups.map((group) => {
    const post = artifact
      ? toPlatformPost(group.messages.map((message) => message.row), artifact, messageView, group.thread.title)
      : {
          platform: GENERIC_PLATFORM,
          label: undefined,
          data: {
            author: { name: group.thread.title },
            messages: group.messages.map((message) => ({
              id: message.id,
              direction: message.direction,
              text: message.body,
              timestamp: message.timestamp ?? undefined,
              sender: message.senderLabel ? { name: message.senderLabel } : undefined,
            })),
          },
        };
    return {
      summary: {
        id: group.thread.key,
        title: group.thread.title,
        subtitle: group.thread.subtitle ?? undefined,
        platform: post.platform,
        label: post.label,
        lastMessage: group.messages.at(-1)?.body,
        timestamp: group.thread.lastActivity ?? undefined,
        count: group.thread.count,
      },
      post,
    };
  });
  const open = threads.find((thread) => thread.summary.id === threadId) ?? (threads.length === 1 ? threads[0] : undefined);
  const hasOlder = hydrated.offset + hydrated.items.length < hydrated.total;
  return {
    threads: threads.map((thread) => thread.summary),
    activeThread: open ? { id: open.summary.id, hasOlder, ...open.post } : undefined,
  };
}

function citedMap(
  artifact: MobileUiConfigArtifact | undefined,
  hydrated: ReturnType<typeof useHydratedArtifactEvidence>,
  citedLabel: string,
): MobileForensicViewModel['map'] {
  const mapView = inferCitationMapView(mapViewOf(artifact), hydrated.displayFields, hydrated.items);
  if (!mapView) return undefined;
  const points = mapRowsToPoints(hydrated.items, mapView, 'mobileArtifactId');
  if (points.length === 0) return undefined;
  return {
    legend: [{ id: 'cited', label: citedLabel, color: 'var(--surface-brand)' }],
    markers: points.map((point) => ({
      id: point.id,
      coordinate: [point.longitude, point.latitude] as [number, number],
      title: point.label ?? undefined,
      legendId: 'cited',
      timestamp: point.timestamp ?? undefined,
    })),
  };
}

function withCitedType(
  families: MobileForensicViewModel['families'],
  family: MobileForensicFamily,
  typeId: string,
  label: string,
  views: MobileForensicViewKind[],
): MobileForensicViewModel['families'] {
  if (families.some((entry) => entry.types.some((type) => type.id === typeId))) return families;
  const type = { id: typeId, label, views };
  if (!families.some((entry) => entry.id === family)) return [...families, { id: family, types: [type] }];
  return families.map((entry) => (
    entry.id === family ? { ...entry, types: [...entry.types, type] } : entry
  ));
}

/** Cited rows in the same `model` / `{ kind }` status shape as the mobile viewer. */
function citationModel(args: {
  artifact: MobileUiConfigArtifact | undefined;
  uiConfig: MobileUiConfigResponse | undefined;
  hydrated: ReturnType<typeof useHydratedArtifactEvidence>;
  artifactType: string;
  view: MobileForensicViewKind;
  threadId: string | undefined;
  citedLabel: string;
  status: MobileForensicStatus | undefined;
}): MobileForensicViewModel {
  const family = citedFamily(args.artifact, args.view);
  const title = args.artifact?.title ?? args.artifactType;
  const messages = citedMessages(args.artifact, args.hydrated, args.threadId);
  const pageSize = hydratePageSize(args.hydrated.limit);
  const page = Math.floor(args.hydrated.offset / pageSize) + 1;
  const base = processedViewModel({
    title,
    uiConfig: args.uiConfig,
    artifactType: args.artifactType,
    view: args.view,
    rows: args.hydrated.items,
    total: args.hydrated.total,
    page,
    pageSize,
    threads: messages.threads,
    activeThread: messages.activeThread,
    status: args.status,
  });
  const views = args.artifact ? artifactViews(args.artifact) : [args.view];
  const availableViews = views.includes(args.view) ? views : [args.view, ...views];
  return {
    ...base,
    mode: 'processed',
    source: 'mobileService',
    title,
    families: withCitedType(base.families, family, args.artifactType, title, availableViews),
    activeFamily: family,
    activeTypeId: args.artifactType,
    activeView: args.view,
    availableViews,
    threads: messages.threads,
    activeThread: messages.activeThread,
    table: base.table ?? fallbackTable(args.hydrated),
    map: args.view === 'map' ? citedMap(args.artifact, args.hydrated, args.citedLabel) : undefined,
    status: args.status,
    record: undefined,
    summary: undefined,
  };
}

function threadIdFromViewerHref(href: string | null | undefined): string | undefined {
  if (!href) return undefined;
  const query = href.split('?')[1];
  if (!query) return undefined;
  return new URLSearchParams(query).get('threadId') ?? undefined;
}

export function CitationConclusion({ text }: { text: string }) {
  return (
    <p data-testid="CitationQueryConclusion" className="m-0 shrink-0 text-sm text-white/90">
      {text}
    </p>
  );
}

/**
 * Source pane for a chat citation that is a mobile evidence locator rather than a file.
 * Renders the cited rows inside MobileForensicViewer. The host owns hydration.
 */
export function MobileEvidenceSourcePane({
  source,
  cellId,
  accessToken,
  onClose,
}: {
  source: Source & { evidence: MobileEvidence };
  cellId: string | null | undefined;
  accessToken: string | null | undefined;
  onClose: () => void;
}) {
  const t = useTimePilotT();
  const themeUfdrEnabled = useMediaPlayerTransitionEnabled('ufdr');
  const hydratedArtifact = useHydratedArtifactEvidence(source, cellId, accessToken, true);
  const { artifact: citedArtifact } = useMobileUiConfigArtifact(hydratedArtifact.graphqlType, true);
  const { data: mobileUiConfig } = useMobileUiConfig(true);
  const showHydratedArtifact = isHydratableArtifactCitation(source);
  const conclusion = citationConclusion(source);
  const artifactType = citedArtifact?.graphql_type ?? hydratedArtifact.graphqlType ?? 'citation';
  const view: MobileForensicViewKind = hydratedArtifact.view === 'messages' || hydratedArtifact.view === 'map'
    ? hydratedArtifact.view
    : 'table';
  const threadId = threadIdFromViewerHref(hydratedArtifact.viewerHref);
  const model = citationModel({
    artifact: citedArtifact,
    uiConfig: mobileUiConfig,
    hydrated: hydratedArtifact,
    artifactType,
    view,
    threadId,
    citedLabel: t('viewers.cited'),
    status: citationStatus(
      hydratedArtifact.status,
      hydratedArtifact.errorMessage,
      t('mobileForensic.loadingCited'),
      t('mobileForensic.citedError'),
      t('mobileForensic.citedEmpty'),
    ),
  });
  const citedChats = view === 'messages' ? publishedChats(model) : undefined;

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
      <div className="flex flex-row items-center justify-end shrink-0">
        <Button
          iconOnly
          size="sm"
          style="text"
          color="light"
          background="inverse"
          aria-label="Close source card"
          data-testid="close-source-button"
          onClick={onClose}
          onTouchEnd={(e) => {
            e.preventDefault();
            onClose();
          }}
        >
          <XIcon />
        </Button>
      </div>
      <div
        data-source-card-scrollport="true"
        className="flex min-h-0 min-w-0 max-h-[calc(100vh-250px)] w-full flex-1 flex-col overflow-y-auto"
      >
        {themeUfdrEnabled ? (
          <>
            <QuerySummaries sources={[source]} uiConfig={mobileUiConfig} />
            {conclusion ? <CitationConclusion text={conclusion} /> : null}
            {showHydratedArtifact ? (
          <UfdrViewer
            compact
            data={{
              tree: [],
              title: model.title,
              chats: citedChats?.chats,
              artifactTypes: model.activeTypeId
                ? [{ id: model.activeTypeId, label: model.title ?? model.activeTypeId, views: [publishedView(model.activeView)] }]
                : undefined,
            }}
            artifactType={model.activeTypeId}
            view={publishedView(model.activeView)}
            focus={model.activeTypeId ? { artifactType: model.activeTypeId, threadId } : undefined}
            status={publishedStatus(model.status)}
            workspace={{
              table: publishedTable(model.table),
              ...(citedChats
                ? {
                    chats: citedChats.chats,
                    openChat: citedChats.openChat,
                    messagePage: model.activeThread
                      ? {
                          hasMore: Boolean(
                            'hasOlder' in model.activeThread && model.activeThread.hasOlder,
                          ),
                          threadId: model.activeThread.id,
                        }
                      : undefined,
                  }
                : {}),
              ...(model.map ? { mapData: model.map } : {}),
            }}
            onQueryChange={(query) => {
              hydratedArtifact.setPage({
                offset: (query.page - 1) * query.pageSize,
                limit: query.pageSize,
              });
            }}
            onLoadMoreMessages={() => {
              hydratedArtifact.setPage({
                offset: hydratedArtifact.offset + hydratedArtifact.items.length,
                limit: hydratePageSize(hydratedArtifact.limit),
                prepend: true,
              });
            }}
          />
            ) : (
              <pre
                data-testid="CitationEvidenceJson"
                className="m-0 min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-all rounded-md bg-white/5 p-3 text-[12px] text-white/90 custom-scrollbar2"
              >
                {formatEvidenceJson(source.evidence)}
              </pre>
            )}
          </>
        ) : showHydratedArtifact && hydratedArtifact.status === 'loading' && hydratedArtifact.item == null ? (
          <div className="flex flex-col gap-2">
            <QuerySummaries sources={[source]} uiConfig={mobileUiConfig} />
            {conclusion ? <CitationConclusion text={conclusion} /> : null}
            <SpinnerGapIcon size={18} className="animate-spin-only" />
          </div>
        ) : showHydratedArtifact ? (
          <CitationHydratedPane
            source={source}
            hydrated={hydratedArtifact}
            artifact={citedArtifact}
            uiConfig={mobileUiConfig}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {conclusion ? <CitationConclusion text={conclusion} /> : null}
            <pre
              data-testid="CitationEvidenceJson"
              className="m-0 min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-all rounded-md bg-white/5 p-3 text-[12px] text-white/90 custom-scrollbar2"
            >
              {formatEvidenceJson(source.evidence)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
